import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import { AnnouncementBanner } from '@/components/banners/AnnouncementBanner'
import { fetchSupportAccess } from '@/reducers/supportAccess/supportAccess.actions'
import { selectSupportWindowOpenForOrg } from '@/reducers/supportAccess/supportAccess.redux'
import { formatCountdown } from '@/utils/formatCountdown'

const POLL_INTERVAL_MS = 60_000

/**
 * @param {object} props
 * @param {number} [props.orgId] Org whose support-access window to watch. When
 *   omitted, falls back to the call-search filter org (the global mount on the
 *   call surfaces). Callers viewing a specific org's content pass it explicitly.
 */
export const SupportAccessBanner = ({ orgId }) => {
  const dispatch = useDispatch()
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const { filters } = useSelector((state) => state.callSearch)
  const { expiresInSeconds } = useSelector((state) => state.supportAccess)
  const organizations = useSelector((state) => state.organizations) || []

  const viewedOrgId = orgId ?? filters?.organizationId
  const windowIsOpenForViewedOrg = useSelector((state) =>
    selectSupportWindowOpenForOrg(state, viewedOrgId)
  )

  const [liveSeconds, setLiveSeconds] = useState(expiresInSeconds)
  const countdownRef = useRef(null)

  const isBaltoAdmin = currentUserOrgId === 1
  const viewedOrg = viewedOrgId
    ? organizations.find((organization) => organization.id === viewedOrgId)
    : null

  const shouldRender = isBaltoAdmin && windowIsOpenForViewedOrg

  useEffect(() => {
    if (!isBaltoAdmin || !viewedOrgId) {
      return undefined
    }

    dispatch(fetchSupportAccess(viewedOrgId))

    const pollInterval = setInterval(() => {
      dispatch(fetchSupportAccess(viewedOrgId))
    }, POLL_INTERVAL_MS)

    return () => clearInterval(pollInterval)
  }, [dispatch, isBaltoAdmin, viewedOrgId])

  useEffect(() => {
    setLiveSeconds(expiresInSeconds)
  }, [expiresInSeconds])

  useEffect(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current)
    }

    if (shouldRender && !isNil(expiresInSeconds) && expiresInSeconds > 0) {
      countdownRef.current = setInterval(() => {
        setLiveSeconds((previous) => {
          if (previous <= 1) {
            clearInterval(countdownRef.current)
            return 0
          }
          return previous - 1
        })
      }, 1000)
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current)
      }
    }
  }, [shouldRender, expiresInSeconds])

  if (!shouldRender) {
    return null
  }

  const orgName = viewedOrg?.name || `Org ${viewedOrgId}`
  const countdownText = !isNil(liveSeconds) ? formatCountdown(liveSeconds) : null

  return (
    <AnnouncementBanner dataTestId="support-access-banner">
      Viewing unredacted data for {orgName}.
      {countdownText && ` Access expires in ${countdownText}.`}
    </AnnouncementBanner>
  )
}
